const DEFAULT_LOCALE = "en-US";

const dateFormats = new Map<string, Intl.DateTimeFormat>();

function dateFormat(
  locale: string,
  name: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const key = `${locale}|${name}`;
  let format = dateFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    dateFormats.set(key, format);
  }
  return format;
}

export function shortDateFmt(locale: string = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return dateFormat(locale, "shortDate", { month: "short", day: "numeric" });
}

export function weekdayDateFmt(locale: string = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return dateFormat(locale, "weekdayDate", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function monthShortFmt(locale: string = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return dateFormat(locale, "monthShort", { month: "short" });
}

export function yearFmt(locale: string = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return dateFormat(locale, "year", { year: "numeric" });
}

export interface SharedDateAxisPart {
  /** Caption for the axis as a whole. */
  caption: string;
  /** Tick labels with the shared part removed. */
  labels: string[];
}

/**
 * Lifts the calendar part every rendered tick has in common out of the tick
 * labels: one shared month becomes the caption "Mar" and leaves the day
 * numbers behind, several months inside one year become the caption "2026"
 * and leave the labels alone, because the year was never in them.
 *
 * Returns null when the ticks straddle years, when there is a single tick, or
 * when the labels do not carry the month they share — hoisting it would then
 * repeat information instead of removing it.
 */
export function sharedDateAxisPart(
  dates: Date[],
  labels: string[],
  locale: string = DEFAULT_LOCALE
): SharedDateAxisPart | null {
  const first = dates[0];
  if (!first || dates.length < 2 || dates.length !== labels.length) {
    return null;
  }

  const year = first.getFullYear();
  if (!dates.every((date) => date.getFullYear() === year)) {
    return null;
  }

  const month = first.getMonth();
  if (!dates.every((date) => date.getMonth() === month)) {
    return { caption: yearFmt(locale).format(first), labels };
  }

  const monthToken = monthShortFmt(locale).format(first);
  if (!labels.every((label) => label.startsWith(monthToken))) {
    return null;
  }

  return {
    caption: monthToken,
    labels: labels.map((label) => label.slice(monthToken.length).trimStart()),
  };
}

export function hmsTimeFmt(locale: string = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return dateFormat(locale, "hmsTime", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

const intFormats = new Map<string, Intl.NumberFormat>();

export function intFmt(value: number, locale: string = DEFAULT_LOCALE): string {
  let format = intFormats.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale);
    intFormats.set(locale, format);
  }
  return format.format(value);
}
